/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  BestTimeDto,
  CreateScheduledPostDto,
  EditPostTextDto,
  EnterPostMetricsDto,
  GetBestPostTimeParams,
  ListScheduledPostsParams,
  ListUpcomingScheduledPostsParams,
  MarkAccountPostedDto,
  MarkPostedDto,
  MoveScheduledPostDto,
  ScheduledPostDto,
  WriteCaptionDto,
  WrittenCaptionDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Posts on the Calendar between two times, oldest first.
 */
export const listScheduledPosts = (
  id: string,
  params: ListScheduledPostsParams,
  signal?: AbortSignal,
) => {
  return customInstance<ScheduledPostDto[]>({
    url: `/api/v1/workspaces/${id}/scheduled-posts`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListScheduledPostsQueryKey = (
  id: string,
  params?: ListScheduledPostsParams,
) => {
  return [
    `/api/v1/workspaces/${id}/scheduled-posts`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListScheduledPostsQueryOptions = <
  TData = Awaited<ReturnType<typeof listScheduledPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: ListScheduledPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listScheduledPosts>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListScheduledPostsQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listScheduledPosts>>> = ({
    signal,
  }) => listScheduledPosts(id, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listScheduledPosts>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListScheduledPostsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listScheduledPosts>>
>;
export type ListScheduledPostsQueryError = ErrorType<ApiErrorDto>;

export function useListScheduledPosts<
  TData = Awaited<ReturnType<typeof listScheduledPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: ListScheduledPostsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listScheduledPosts>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listScheduledPosts>>,
          TError,
          Awaited<ReturnType<typeof listScheduledPosts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListScheduledPosts<
  TData = Awaited<ReturnType<typeof listScheduledPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: ListScheduledPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listScheduledPosts>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listScheduledPosts>>,
          TError,
          Awaited<ReturnType<typeof listScheduledPosts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListScheduledPosts<
  TData = Awaited<ReturnType<typeof listScheduledPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: ListScheduledPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listScheduledPosts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Posts on the Calendar between two times, oldest first.
 */

export function useListScheduledPosts<
  TData = Awaited<ReturnType<typeof listScheduledPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: ListScheduledPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listScheduledPosts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListScheduledPostsQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Make a post by hand: to one or more accounts, now, later or as a draft.
 */
export const createScheduledPost = (
  id: string,
  createScheduledPostDto: BodyType<CreateScheduledPostDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ScheduledPostDto>({
    url: `/api/v1/workspaces/${id}/scheduled-posts`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createScheduledPostDto,
    signal,
  });
};

export const getCreateScheduledPostMutationKey = () => ['createScheduledPost'] as const;

export const getCreateScheduledPostMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createScheduledPost>>,
    TError,
    CreateScheduledPostMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createScheduledPost>>,
  TError,
  CreateScheduledPostMutationVariables,
  TContext
> => {
  const mutationKey = getCreateScheduledPostMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createScheduledPost>>,
    CreateScheduledPostMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createScheduledPost(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateScheduledPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof createScheduledPost>>
>;
export type CreateScheduledPostMutationBody = BodyType<CreateScheduledPostDto>;
export type CreateScheduledPostMutationError = ErrorType<ApiErrorDto>;
export type CreateScheduledPostMutationVariables = {
  id: string;
  data: BodyType<CreateScheduledPostDto>;
};

/**
 * @summary Make a post by hand: to one or more accounts, now, later or as a draft.
 */
export const useCreateScheduledPost = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createScheduledPost>>,
      TError,
      CreateScheduledPostMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createScheduledPost>>,
  TError,
  CreateScheduledPostMutationVariables,
  TContext
> => {
  return useMutation(getCreateScheduledPostMutationOptions(options), queryClient);
};
/**
 * @summary Home's "Coming up": the next scheduled posts, soonest first (a crosspost counts once).
 */
export const listUpcomingScheduledPosts = (
  id: string,
  params?: ListUpcomingScheduledPostsParams,
  signal?: AbortSignal,
) => {
  return customInstance<ScheduledPostDto[]>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/upcoming`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListUpcomingScheduledPostsQueryKey = (
  id: string,
  params?: ListUpcomingScheduledPostsParams,
) => {
  return [
    `/api/v1/workspaces/${id}/scheduled-posts/upcoming`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListUpcomingScheduledPostsQueryOptions = <
  TData = Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListUpcomingScheduledPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListUpcomingScheduledPostsQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listUpcomingScheduledPosts>>
  > = ({ signal }) => listUpcomingScheduledPosts(id, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListUpcomingScheduledPostsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listUpcomingScheduledPosts>>
>;
export type ListUpcomingScheduledPostsQueryError = ErrorType<ApiErrorDto>;

export function useListUpcomingScheduledPosts<
  TData = Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: undefined | ListUpcomingScheduledPostsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
          TError,
          Awaited<ReturnType<typeof listUpcomingScheduledPosts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListUpcomingScheduledPosts<
  TData = Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListUpcomingScheduledPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
          TError,
          Awaited<ReturnType<typeof listUpcomingScheduledPosts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListUpcomingScheduledPosts<
  TData = Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListUpcomingScheduledPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Home's "Coming up": the next scheduled posts, soonest first (a crosspost counts once).
 */

export function useListUpcomingScheduledPosts<
  TData = Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListUpcomingScheduledPostsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listUpcomingScheduledPosts>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListUpcomingScheduledPostsQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary The best free time on a day for the accounts' audiences.
 */
export const getBestPostTime = (
  id: string,
  params: GetBestPostTimeParams,
  signal?: AbortSignal,
) => {
  return customInstance<BestTimeDto>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/best-time`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetBestPostTimeQueryKey = (
  id: string,
  params?: GetBestPostTimeParams,
) => {
  return [
    `/api/v1/workspaces/${id}/scheduled-posts/best-time`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetBestPostTimeQueryOptions = <
  TData = Awaited<ReturnType<typeof getBestPostTime>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: GetBestPostTimeParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBestPostTime>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBestPostTimeQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBestPostTime>>> = ({
    signal,
  }) => getBestPostTime(id, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getBestPostTime>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetBestPostTimeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBestPostTime>>
>;
export type GetBestPostTimeQueryError = ErrorType<ApiErrorDto>;

export function useGetBestPostTime<
  TData = Awaited<ReturnType<typeof getBestPostTime>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: GetBestPostTimeParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBestPostTime>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBestPostTime>>,
          TError,
          Awaited<ReturnType<typeof getBestPostTime>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBestPostTime<
  TData = Awaited<ReturnType<typeof getBestPostTime>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: GetBestPostTimeParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBestPostTime>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBestPostTime>>,
          TError,
          Awaited<ReturnType<typeof getBestPostTime>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBestPostTime<
  TData = Awaited<ReturnType<typeof getBestPostTime>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: GetBestPostTimeParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBestPostTime>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The best free time on a day for the accounts' audiences.
 */

export function useGetBestPostTime<
  TData = Awaited<ReturnType<typeof getBestPostTime>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: GetBestPostTimeParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBestPostTime>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetBestPostTimeQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Write a caption and hashtags from the brand profile.
 */
export const writePostCaption = (
  id: string,
  writeCaptionDto: BodyType<WriteCaptionDto>,
  signal?: AbortSignal,
) => {
  return customInstance<WrittenCaptionDto>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/caption`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: writeCaptionDto,
    signal,
  });
};

export const getWritePostCaptionMutationKey = () => ['writePostCaption'] as const;

export const getWritePostCaptionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof writePostCaption>>,
    TError,
    WritePostCaptionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof writePostCaption>>,
  TError,
  WritePostCaptionMutationVariables,
  TContext
> => {
  const mutationKey = getWritePostCaptionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof writePostCaption>>,
    WritePostCaptionMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return writePostCaption(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type WritePostCaptionMutationResult = NonNullable<
  Awaited<ReturnType<typeof writePostCaption>>
>;
export type WritePostCaptionMutationBody = BodyType<WriteCaptionDto>;
export type WritePostCaptionMutationError = ErrorType<ApiErrorDto>;
export type WritePostCaptionMutationVariables = {
  id: string;
  data: BodyType<WriteCaptionDto>;
};

/**
 * @summary Write a caption and hashtags from the brand profile.
 */
export const useWritePostCaption = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof writePostCaption>>,
      TError,
      WritePostCaptionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof writePostCaption>>,
  TError,
  WritePostCaptionMutationVariables,
  TContext
> => {
  return useMutation(getWritePostCaptionMutationOptions(options), queryClient);
};
/**
 * @summary Edit a draft; `draft: false` schedules it.
 */
export const updateDraftPost = (
  id: string,
  postId: string,
  createScheduledPostDto: BodyType<CreateScheduledPostDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ScheduledPostDto>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/${postId}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: createScheduledPostDto,
    signal,
  });
};

export const getUpdateDraftPostMutationKey = () => ['updateDraftPost'] as const;

export const getUpdateDraftPostMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateDraftPost>>,
    TError,
    UpdateDraftPostMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateDraftPost>>,
  TError,
  UpdateDraftPostMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateDraftPostMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateDraftPost>>,
    UpdateDraftPostMutationVariables
  > = (props) => {
    const { id, postId, data } = props ?? {};

    return updateDraftPost(id, postId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateDraftPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateDraftPost>>
>;
export type UpdateDraftPostMutationBody = BodyType<CreateScheduledPostDto>;
export type UpdateDraftPostMutationError = ErrorType<ApiErrorDto>;
export type UpdateDraftPostMutationVariables = {
  id: string;
  postId: string;
  data: BodyType<CreateScheduledPostDto>;
};

/**
 * @summary Edit a draft; `draft: false` schedules it.
 */
export const useUpdateDraftPost = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateDraftPost>>,
      TError,
      UpdateDraftPostMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateDraftPost>>,
  TError,
  UpdateDraftPostMutationVariables,
  TContext
> => {
  return useMutation(getUpdateDraftPostMutationOptions(options), queryClient);
};
/**
 * @summary Move a post (every account it crossposts to) to another time. A post that
failed is scheduled again, and looked for after its new time.
 */
export const moveScheduledPost = (
  id: string,
  postId: string,
  moveScheduledPostDto: BodyType<MoveScheduledPostDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ScheduledPostDto>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/${postId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: moveScheduledPostDto,
    signal,
  });
};

export const getMoveScheduledPostMutationKey = () => ['moveScheduledPost'] as const;

export const getMoveScheduledPostMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof moveScheduledPost>>,
    TError,
    MoveScheduledPostMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof moveScheduledPost>>,
  TError,
  MoveScheduledPostMutationVariables,
  TContext
> => {
  const mutationKey = getMoveScheduledPostMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof moveScheduledPost>>,
    MoveScheduledPostMutationVariables
  > = (props) => {
    const { id, postId, data } = props ?? {};

    return moveScheduledPost(id, postId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type MoveScheduledPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof moveScheduledPost>>
>;
export type MoveScheduledPostMutationBody = BodyType<MoveScheduledPostDto>;
export type MoveScheduledPostMutationError = ErrorType<ApiErrorDto>;
export type MoveScheduledPostMutationVariables = {
  id: string;
  postId: string;
  data: BodyType<MoveScheduledPostDto>;
};

/**
 * @summary Move a post (every account it crossposts to) to another time. A post that
failed is scheduled again, and looked for after its new time.
 */
export const useMoveScheduledPost = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof moveScheduledPost>>,
      TError,
      MoveScheduledPostMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof moveScheduledPost>>,
  TError,
  MoveScheduledPostMutationVariables,
  TContext
> => {
  return useMutation(getMoveScheduledPostMutationOptions(options), queryClient);
};
/**
 * @summary Take a post off the Calendar.
 */
export const unschedulePost = (id: string, postId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/${postId}`,
    method: 'DELETE',
    signal,
  });
};

export const getUnschedulePostMutationKey = () => ['unschedulePost'] as const;

export const getUnschedulePostMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof unschedulePost>>,
    TError,
    UnschedulePostMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof unschedulePost>>,
  TError,
  UnschedulePostMutationVariables,
  TContext
> => {
  const mutationKey = getUnschedulePostMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof unschedulePost>>,
    UnschedulePostMutationVariables
  > = (props) => {
    const { id, postId } = props ?? {};

    return unschedulePost(id, postId);
  };

  return { mutationFn, ...mutationOptions };
};

export type UnschedulePostMutationResult = NonNullable<
  Awaited<ReturnType<typeof unschedulePost>>
>;

export type UnschedulePostMutationError = ErrorType<ApiErrorDto>;
export type UnschedulePostMutationVariables = { id: string; postId: string };

/**
 * @summary Take a post off the Calendar.
 */
export const useUnschedulePost = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof unschedulePost>>,
      TError,
      UnschedulePostMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof unschedulePost>>,
  TError,
  UnschedulePostMutationVariables,
  TContext
> => {
  return useMutation(getUnschedulePostMutationOptions(options), queryClient);
};
/**
 * @summary Change a post's title or caption before it goes out: the shared one, or
a platform's own. Accounts it already went out on keep theirs.
 */
export const editPostText = (
  id: string,
  postId: string,
  editPostTextDto: BodyType<EditPostTextDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ScheduledPostDto>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/${postId}/text`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: editPostTextDto,
    signal,
  });
};

export const getEditPostTextMutationKey = () => ['editPostText'] as const;

export const getEditPostTextMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof editPostText>>,
    TError,
    EditPostTextMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof editPostText>>,
  TError,
  EditPostTextMutationVariables,
  TContext
> => {
  const mutationKey = getEditPostTextMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof editPostText>>,
    EditPostTextMutationVariables
  > = (props) => {
    const { id, postId, data } = props ?? {};

    return editPostText(id, postId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type EditPostTextMutationResult = NonNullable<
  Awaited<ReturnType<typeof editPostText>>
>;
export type EditPostTextMutationBody = BodyType<EditPostTextDto>;
export type EditPostTextMutationError = ErrorType<ApiErrorDto>;
export type EditPostTextMutationVariables = {
  id: string;
  postId: string;
  data: BodyType<EditPostTextDto>;
};

/**
 * @summary Change a post's title or caption before it goes out: the shared one, or
a platform's own. Accounts it already went out on keep theirs.
 */
export const useEditPostText = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof editPostText>>,
      TError,
      EditPostTextMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof editPostText>>,
  TError,
  EditPostTextMutationVariables,
  TContext
> => {
  return useMutation(getEditPostTextMutationOptions(options), queryClient);
};
/**
 * @summary Record that a scheduled post went out (you posted it yourself), now or earlier.
 */
export const markPostPosted = (
  id: string,
  postId: string,
  markPostedDto: BodyType<MarkPostedDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ScheduledPostDto>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/${postId}/posted`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: markPostedDto,
    signal,
  });
};

export const getMarkPostPostedMutationKey = () => ['markPostPosted'] as const;

export const getMarkPostPostedMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markPostPosted>>,
    TError,
    MarkPostPostedMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof markPostPosted>>,
  TError,
  MarkPostPostedMutationVariables,
  TContext
> => {
  const mutationKey = getMarkPostPostedMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof markPostPosted>>,
    MarkPostPostedMutationVariables
  > = (props) => {
    const { id, postId, data } = props ?? {};

    return markPostPosted(id, postId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type MarkPostPostedMutationResult = NonNullable<
  Awaited<ReturnType<typeof markPostPosted>>
>;
export type MarkPostPostedMutationBody = BodyType<MarkPostedDto>;
export type MarkPostPostedMutationError = ErrorType<ApiErrorDto>;
export type MarkPostPostedMutationVariables = {
  id: string;
  postId: string;
  data: BodyType<MarkPostedDto>;
};

/**
 * @summary Record that a scheduled post went out (you posted it yourself), now or earlier.
 */
export const useMarkPostPosted = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof markPostPosted>>,
      TError,
      MarkPostPostedMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof markPostPosted>>,
  TError,
  MarkPostPostedMutationVariables,
  TContext
> => {
  return useMutation(getMarkPostPostedMutationOptions(options), queryClient);
};
/**
 * @summary Record that a post went out on one of its accounts (you posted it there yourself).
 */
export const markAccountPosted = (
  id: string,
  postId: string,
  accountId: string,
  markAccountPostedDto: BodyType<MarkAccountPostedDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ScheduledPostDto>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/${postId}/accounts/${accountId}/posted`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: markAccountPostedDto,
    signal,
  });
};

export const getMarkAccountPostedMutationKey = () => ['markAccountPosted'] as const;

export const getMarkAccountPostedMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markAccountPosted>>,
    TError,
    MarkAccountPostedMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof markAccountPosted>>,
  TError,
  MarkAccountPostedMutationVariables,
  TContext
> => {
  const mutationKey = getMarkAccountPostedMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof markAccountPosted>>,
    MarkAccountPostedMutationVariables
  > = (props) => {
    const { id, postId, accountId, data } = props ?? {};

    return markAccountPosted(id, postId, accountId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type MarkAccountPostedMutationResult = NonNullable<
  Awaited<ReturnType<typeof markAccountPosted>>
>;
export type MarkAccountPostedMutationBody = BodyType<MarkAccountPostedDto>;
export type MarkAccountPostedMutationError = ErrorType<ApiErrorDto>;
export type MarkAccountPostedMutationVariables = {
  id: string;
  postId: string;
  accountId: string;
  data: BodyType<MarkAccountPostedDto>;
};

/**
 * @summary Record that a post went out on one of its accounts (you posted it there yourself).
 */
export const useMarkAccountPosted = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof markAccountPosted>>,
      TError,
      MarkAccountPostedMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof markAccountPosted>>,
  TError,
  MarkAccountPostedMutationVariables,
  TContext
> => {
  return useMutation(getMarkAccountPostedMutationOptions(options), queryClient);
};
/**
 * @summary Enter how a post did (views, watch time, saves, shares).
 */
export const enterPostMetrics = (
  id: string,
  postId: string,
  enterPostMetricsDto: BodyType<EnterPostMetricsDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ScheduledPostDto>({
    url: `/api/v1/workspaces/${id}/scheduled-posts/${postId}/metrics`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: enterPostMetricsDto,
    signal,
  });
};

export const getEnterPostMetricsMutationKey = () => ['enterPostMetrics'] as const;

export const getEnterPostMetricsMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof enterPostMetrics>>,
    TError,
    EnterPostMetricsMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof enterPostMetrics>>,
  TError,
  EnterPostMetricsMutationVariables,
  TContext
> => {
  const mutationKey = getEnterPostMetricsMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof enterPostMetrics>>,
    EnterPostMetricsMutationVariables
  > = (props) => {
    const { id, postId, data } = props ?? {};

    return enterPostMetrics(id, postId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type EnterPostMetricsMutationResult = NonNullable<
  Awaited<ReturnType<typeof enterPostMetrics>>
>;
export type EnterPostMetricsMutationBody = BodyType<EnterPostMetricsDto>;
export type EnterPostMetricsMutationError = ErrorType<ApiErrorDto>;
export type EnterPostMetricsMutationVariables = {
  id: string;
  postId: string;
  data: BodyType<EnterPostMetricsDto>;
};

/**
 * @summary Enter how a post did (views, watch time, saves, shares).
 */
export const useEnterPostMetrics = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof enterPostMetrics>>,
      TError,
      EnterPostMetricsMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof enterPostMetrics>>,
  TError,
  EnterPostMetricsMutationVariables,
  TContext
> => {
  return useMutation(getEnterPostMetricsMutationOptions(options), queryClient);
};
